import { Activity, AlignLeft, CalendarClock, CalendarPlus, GitCommitHorizontal, KeyRound, Pencil, ShieldCheck, Tag, Target, UserRound, Users, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoNote, Panel } from '@/components/common/Panel'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { fmtDateTime } from '../../shared/format'
import type { Role } from '../types'
import { useAccessLookups } from '../useLookups'
import { ScopeBadge, StatusBadge } from './AccessBits'

type Tone = 'blue' | 'orange'

function Field({ icon: Icon, label, children, className }: { icon: LucideIcon; label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-xl border border-border/70 bg-muted/25 px-3.5 py-3 dark:bg-muted/15', className)}>
      <dt className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Icon className="size-3.5" />
        {label}
      </dt>
      <dd className="mt-1.5 break-words text-sm font-medium">{children}</dd>
    </div>
  )
}

function Stat({ icon: Icon, tone, label, value }: { icon: LucideIcon; tone: Tone; label: string; value: number }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border p-4',
        tone === 'blue' ? 'border-primary/15 bg-primary/[0.05] dark:bg-primary/10' : 'border-brand-orange/20 bg-brand-orange/[0.06] dark:bg-brand-orange/10',
      )}
    >
      <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg border bg-card sm:size-10', tone === 'blue' ? 'border-primary/15 text-primary' : 'border-brand-orange/20 text-brand-orange')}>
        <Icon className="size-4 sm:size-5" />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold leading-tight tabular-nums">{value}</p>
      </div>
    </div>
  )
}

export function RoleGeneralCard({ role, canWrite, onEdit }: { role: Role; canWrite: boolean; onEdit: () => void }) {
  const { t } = useTranslation()
  const { targetNames } = useAccessLookups()
  const targets = targetNames(role)

  return (
    <Panel
        icon={ShieldCheck}
        tone="blue"
        title={t('access.detail.tabs.general')}
        hint={t('access.detail.generalHint')}
        actions={
          canWrite ? (
            <Button size="sm" variant="outline" onClick={onEdit}>
              <Pencil />
              {t('access.detail.editInfo')}
            </Button>
          ) : null
        }
        bodyClassName="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]"
      >
        <dl className="grid content-start gap-3 sm:grid-cols-2">
          <Field icon={Tag} label={t('access.roles.columns.name')}>
            {role.name}
          </Field>
          <Field icon={Activity} label={t('access.roles.columns.status')}>
            <StatusBadge status={role.status} />
          </Field>
          <Field icon={AlignLeft} label={t('access.roles.columns.description')} className="sm:col-span-2">
            <span className="font-normal leading-relaxed">{role.description}</span>
          </Field>
          <Field icon={Target} label={t('access.scope.label')} className="sm:col-span-2">
            <span className="flex flex-wrap items-center gap-2">
              <ScopeBadge scope={role.scope} />
              <span className="font-normal text-muted-foreground">{targets.length ? targets.join(', ') : t(`access.scope.${role.scope}Hint`)}</span>
            </span>
          </Field>
        </dl>
        <div className="grid content-start gap-3 sm:grid-cols-2 lg:grid-cols-1">
          <Stat icon={KeyRound} tone="blue" label={t('access.roles.columns.permissions')} value={role.permissions.length} />
          <Stat icon={Users} tone="orange" label={t('access.roles.columns.users')} value={role.assignments.length} />
        </div>
    </Panel>
  )
}

function actorLabel(actor: Role['createdBy']) {
  if (!actor) return '—'
  return actor.fullName || actor.email || '—'
}

export function RoleVersionCard({ role }: { role: Role }) {
  const { t } = useTranslation()
  return (
    <Panel
        icon={GitCommitHorizontal}
        tone="orange"
        title={t('access.detail.tabs.version')}
        hint={t('access.detail.versionHint')}
        actions={
          <span className="inline-flex items-baseline gap-1.5 rounded-lg border border-brand-orange/20 bg-brand-orange/[0.08] px-3 py-1.5">
            <span className="text-xs font-medium text-muted-foreground">{t('access.detail.currentVersion')}</span>
            <span className="text-lg font-semibold tabular-nums text-brand-orange">v{role.version}</span>
          </span>
        }
        bodyClassName="space-y-4"
      >
        <dl className="grid gap-3 sm:grid-cols-2">
          <Field icon={CalendarPlus} label={t('access.detail.createdAt')}>
            <span className="tabular-nums">{fmtDateTime(role.createdAt)}</span>
          </Field>
          <Field icon={UserRound} label={t('access.detail.createdBy')}>
            <span>{actorLabel(role.createdBy)}</span>
            {role.createdBy?.email ? <span className="mt-0.5 block text-xs font-normal text-muted-foreground">{role.createdBy.email}</span> : null}
          </Field>
          <Field icon={CalendarClock} label={t('access.roles.columns.updated')}>
            <span className="tabular-nums">{fmtDateTime(role.updatedAt)}</span>
          </Field>
          <Field icon={UserRound} label={t('access.detail.updatedBy')}>
            <span>{actorLabel(role.updatedBy)}</span>
            {role.updatedBy?.email ? <span className="mt-0.5 block text-xs font-normal text-muted-foreground">{role.updatedBy.email}</span> : null}
          </Field>
        </dl>
        <InfoNote>{t('access.detail.versionNote')}</InfoNote>
    </Panel>
  )
}
